import Vue from "vue";

const defaultCreditTransactionDetails = {
  credit_detail: {
    id: 0,
    client_id: 0,
    session_credits: { expiring_soon: 0, available: 0, used: 0 },
    reschedule_credits: { expiring_soon: 0, available: 0, used: 0 },
    scheduled_credits: { total_credits: 0, total_sessions: 0 },
    current_balance: 0,
    unused_credits: 0
  },
  available_items: [],
  reschedule_credits: []
};

export const CreditState = {
  creditPackageItem: null,
  creditPackageItems: [],
  totalCreditPackageItems: 0,
  //
  creditPackage: null,
  creditPackages: [],
  totalCreditPackages: 0,
  //
  emptyCreditTransactionDetails: defaultCreditTransactionDetails,
  creditTransactionDetails: defaultCreditTransactionDetails,
  creditTransaction: null,
  creditTransactions: [],
  totalCreditTransactions: 0,
  //
  creditsManagement: [], // list of client
  totalCreditsManagement: 0
};

export const CreditGetters = {
  creditPackageItem: state => {
    return state.creditPackageItem;
  },
  creditPackageItems: state => {
    return state.creditPackageItems;
  },
  totalCreditPackageItems: state => {
    return state.totalCreditPackageItems;
  },
  //
  creditPackage: state => {
    return state.creditPackage;
  },
  creditPackages: state => {
    return state.creditPackages;
  },
  totalCreditPackages: state => {
    return state.totalCreditPackages;
  },
  //
  creditTransactionDetails: state => {
    return state.creditTransactionDetails;
  },
  creditTransaction: state => {
    return state.creditTransaction;
  },
  creditTransactions: state => {
    return state.creditTransactions;
  },
  totalCreditTransactions: state => {
    return state.totalCreditTransactions;
  },
  //
  creditsManagement: state => {
    return state.creditsManagement;
  },
  totalCreditsManagement: state => {
    return state.totalCreditsManagement;
  }
};

export const CreditMutations = {
  setCreditPackageItem: (state, data) => {
    if (data) {
      const index = state.creditPackageItems.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (index > -1) {
        const arr = [...state.creditPackageItems];
        arr[index] = data;
        Vue.set(state, "creditPackageItems", arr);
      }
    }
    Vue.set(state, "creditPackageItem", data);
  },
  setCreditPackageItems: (state, data) => {
    Vue.set(state, "creditPackageItems", data.package_items);
    Vue.set(state, "totalCreditPackageItems", data.total_count);
  },
  addCreditPackageItem: (state, data) => {
    const total = state.totalCreditPackageItems + 1;
    Vue.set(state, "creditPackageItems", [...state.creditPackageItems, data]);
    Vue.set(state, "totalCreditPackageItems", total);
  },
  removeCreditPackageItem: (state, data) => {
    const arr = state.creditPackageItems.filter(
      i => Number(i.id) !== Number(data.id)
    );
    const total = state.totalCreditPackageItems - 1;
    Vue.set(state, "creditPackageItems", arr);
    Vue.set(state, "totalCreditPackageItems", total);
  },
  //
  setCreditPackage: (state, data) => {
    if (data) {
      const index = state.creditPackages.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (index > -1) {
        const arr = [...state.creditPackages];
        arr[index] = data;
        Vue.set(state, "creditPackages", arr);
      }
    }
    Vue.set(state, "creditPackage", data);
  },
  setCreditPackages: (state, data) => {
    Vue.set(state, "creditPackages", data.packages);
    Vue.set(state, "totalCreditPackages", data.total_count);
  },
  addCreditPackage: (state, data) => {
    Vue.set(state, "creditPackages", [...state.creditPackages, data]);
    Vue.set(state, "totalCreditPackages", state.totalCreditPackages + 1);
  },
  removeCreditPackage: (state, data) => {
    const arr = state.creditPackages.filter(
      i => Number(i.id) !== Number(data.id)
    );
    Vue.set(state, "creditPackages", arr);
    Vue.set(state, "totalCreditPackages", state.totalCreditPackages - 1);
  },
  //
  setCreditTransactionDetails: (state, data) => {
    Vue.set(state, "creditTransactionDetails", data);
  },
  setCreditTransaction: (state, data) => {
    Vue.set(state, "creditTransaction", data);
  },
  setCreditTransactions: (state, data) => {
    Vue.set(state, "creditTransactions", data.credit_transactions);
    Vue.set(state, "totalCreditTransactions", data.total_count);
  },
  addCreditTransaction: (state, data) => {
    Vue.set(state, "creditTransactions", [data, ...state.creditTransactions]);
    const n = state.totalCreditTransactions + 1;
    Vue.set(state, "totalCreditTransactions", n);
  },
  //
  setCreditsManagement: (state, data) => {
    Vue.set(state, "creditsManagement", data.clients);
    Vue.set(state, "totalCreditsManagement", data.total_clients);
  }
};

export const CreditActions = {
  getCreditPackageItems: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/package_items${params}`)
      .then(response => {
        commit("setCreditPackageItems", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message =
          err || "Unable to fetch credit package items from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getCreditPackageItem: ({ commit, dispatch }, data = {}) => {
    if (!data.id) {
      commit("setCreditPackageItem", null);
      return { success: false, data: null };
    }
    return Vue.axios.secured
      .get(`/api/v1/package_items/${data.id}`)
      .then(response => {
        commit("setCreditPackageItem", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message =
          err || "Unable to fetch credit package item from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createCreditPackageItem: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/package_items`, { package_item: data.package_item })
      .then(response => {
        commit("addCreditPackageItem", response.data);
        const name = response.data.name;
        const title = "New package item created";
        const message = `Success! The <b>${name}</b> has been created.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create credit package item.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateCreditPackageItem: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/package_items/${data.id}`, {
        package_item: data.package_item
      })
      .then(response => {
        commit("setCreditPackageItem", response.data);
        const name = response.data.name;
        const title = data.title || "Package item updated";
        const message =
          data.message || `Success! The <b>${name}</b> has been updated.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update credit package item.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteCreditPackageItem: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/package_items/${data.id}`)
      .then(response => {
        commit("removeCreditPackageItem", data);
        const name = data.name;
        const title = "Package item deleted";
        const message = `Success! The <b>${name}</b> has been deleted from the center.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete credit package item.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  //
  getCreditPackages: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/packages${params}`)
      .then(response => {
        commit("setCreditPackages", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch credit packages from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getCreditPackage: ({ commit, dispatch }, data = {}) => {
    if (!data.id) {
      commit("setCreditPackage", null);
      return { success: false, data: null };
    }
    return Vue.axios.secured
      .get(`/api/v1/packages/${data.id}`)
      .then(response => {
        commit("setCreditPackage", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch credit package from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createCreditPackage: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/packages`, { package: data.package })
      .then(response => {
        commit("addCreditPackage", response.data);
        const name = response.data.name;
        const title = "New package created";
        const message = `Success! The <b>${name}</b> has been created.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create credit package.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateCreditPackage: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/packages/${data.id}`, { package: data.package })
      .then(response => {
        commit("setCreditPackage", response.data);
        const name = response.data.name;
        const title = data.title || "Package updated";
        const message =
          data.message || `Success! The <b>${name}</b> has been updated.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update credit package.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteCreditPackage: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/packages/${data.id}`)
      .then(response => {
        commit("removeCreditPackage", data);
        const name = data.name;
        const title = "Package deleted";
        const message = `Success! The <b>${name}</b> has been deleted from the center.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete credit package.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  //
  getCreditTransactions: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/clients/${data.client_id}/credit_transactions${params}`)
      .then(response => {
        if (data.search) {
          return { success: true, data: response.data };
        }
        commit("setCreditTransactions", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message =
          err || "Unable to fetch credit transactions from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getCreditTransaction: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .get(`/api/v1/clients/${data.client_id}/credit_transactions/${data.id}`)
      .then(response => {
        commit("setCreditTransaction", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message =
          err || "Unable to fetch credit transaction from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getCreditTransactionDetails: ({ state, commit, dispatch }, data = {}) => {
    commit("setCreditTransactionDetails", state.emptyCreditTransactionDetails);
    const params = data.params || "";
    return Vue.axios.secured
      .get(
        `/api/v1/clients/${data.client_id}/credit_transaction_details${params}`
      )
      .then(response => {
        if (response.data) {
          commit("setCreditTransactionDetails", response.data);
          return { success: true, data: response.data };
        }
        return { success: false, data: null };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message =
          err || "Unable to fetch credit transaction details from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  exportCreditTransactions: ({ dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(
        `/api/v1/clients/${data.client_id}/export_credit_transactions${params}`,
        { responseType: "blob" }
      )
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to export credit transactions.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createCreditTransaction: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/clients/${data.client_id}/credit_transactions`, {
        client_id: data.client_id,
        credit_transaction: data.credit_transaction
      })
      .then(async response => {
        if (response.data.credit_transaction) {
          commit("addCreditTransaction", response.data.credit_transaction);
        }
        await dispatch("getCreditTransactionDetails", data);
        const title = "New transaction added";
        const message = "Success! transaction has been added.";
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create credit transaction.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createCreditTransactionWithPackage: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(
        `/api/v1/clients/${data.client_id}/credit_transactions_with_package`,
        { package_id: data.package_id }
      )
      .then(async response => {
        commit("addCreditTransaction", response.data.credit_transaction);
        await dispatch("getCreditTransactionDetails", data);
        const title = "New transaction added";
        const message = `Success! <b>${data.package_credit} credit(s) from ${data.package_name}</b> has been added.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create credit transaction.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  //
  getCreditsManagement: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/credits_management${params}`)
      .then(response => {
        commit("setCreditsManagement", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message =
          err || "Unable to fetch credits management from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/credit.store.js